/* eslint react-hooks/exhaustive-deps: "warn" */
import { type ReactNode, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Flag } from 'ui/atoms/Flag/Flag';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './LegalLifeOfMatter.module.scss';

type Translate = (key: string) => string;

type TagType = 'ok' | 'warn' | 'hit' | 'info';

type Tag = {
  text: string;
  type: TagType;
};

type Row = {
  k: string;
  v: ReactNode;
  note?: string;
  tag?: Tag;
};

type TreeNode = {
  code: string;
  name: string;
  note: string;
  depth: 1 | 2 | 3;
};

type Panel = {
  label: string;
  live?: boolean;
  rtFlag?: string;
  rt: string;
  tree?: TreeNode[];
  rows: Row[];
};

type Step = {
  k: string;
  t: string;
  d: string;
};

const getSteps = (t: Translate): Step[] => [
  {
    k: t('Step 01'),
    t: t('Intake and conflicts'),
    d: t(
      'Before the engagement letter goes out, the name on the enquiry is resolved into every entity in its corporate family. Conflicts then run against the group rather than the one company the client happened to name.',
    ),
  },
  {
    k: t('Step 02'),
    t: t('Entity identification'),
    d: t(
      'With the group resolved, the entity itself is pinned down. Registered number, address for service, officers as at the date, charges and status history. The facts a pleading or a certificate has to survive.',
    ),
  },
  {
    k: t('Step 03'),
    t: t('Ownership and control'),
    d: t(
      'The chain above the entity is walked registry by registry until it reaches natural persons, or a register that publishes nothing. Every holding carries the filing behind it, and derived percentages show their working.',
    ),
  },
  {
    k: t('Step 04'),
    t: t('PEPs and sanctions'),
    d: t(
      'Screening runs across the group resolved in the previous step, not the subject alone, because designation attaches to ownership. Each hit arrives with the list entry it matched and the date that entry was published.',
    ),
  },
  {
    k: t('Step 05'),
    t: t('Company financials'),
    d: t(
      'Filed accounts across that same group answer the question that decides whether the matter is worth running. Solvency, security already granted, and whether recovery stays proportionate to the costs.',
    ),
  },
  {
    k: t('Step 06'),
    t: t('Perpetual monitoring'),
    d: t(
      'Every entity identified in the five steps above stays under watch for the life of the retainer. Officer changes, share transfers, new charges, insolvency and status changes arrive as they are filed.',
    ),
  },
];

const getProof = (t: Translate): string[] => [
  t('Sourced directly from government registries'),
  t('Source name and link on every dataset'),
  t('Retrieval timestamp on every field'),
  t('The original filing attached'),
  t('12 years sourcing registry data'),
];

const PANELS: Panel[] = [
  {
    label: 'Conflicts expansion',
    rt: '11 entities · 6 jurisdictions',
    tree: [
      { depth: 1, code: 'lu', name: 'Verrick Holdings S.à r.l.', note: 'Ultimate parent' },
      { depth: 2, code: 'nl', name: 'Northgate Logistics B.V.', note: '100%' },
      { depth: 3, code: 'gb', name: 'Northgate Logistics Limited', note: '100%' },
      { depth: 3, code: 'gb', name: 'Northgate Freight Services Ltd', note: '100%' },
      { depth: 3, code: 'ie', name: 'Northgate Cold Chain Ltd', note: '75%' },
      { depth: 2, code: 'de', name: 'Verrick Real Assets GmbH', note: '100%' },
    ],
    rows: [
      {
        k: 'Cross-check',
        v: 'Verrick Real Assets GmbH appears in your matter index as an opposing party',
        note: 'Matter ref. 2024-0918 · flagged before engagement',
        tag: { text: 'Review', type: 'warn' },
      },
    ],
  },
  {
    label: 'Entity record',
    rtFlag: 'gb',
    rt: 'Companies House',
    rows: [
      {
        k: 'Legal name',
        v: 'NORTHGATE LOGISTICS LIMITED',
        note: 'Company no. 08842119 · incorporated 21 Jan 2014',
        tag: { text: 'Active', type: 'ok' },
      },
      {
        k: 'Registered office',
        v: '14 Wellington Place, Leeds LS1 4AP',
        note: 'Effective from 03 Apr 2023 · address for service',
      },
      {
        k: 'Trading address',
        v: 'Unit 7, Trafford Park, Manchester M17',
        note: 'Correspondence only — service here is not valid service',
        tag: { text: 'Note', type: 'info' },
      },
      {
        k: 'Officers',
        v: '4 current, 6 resigned',
        note: 'Latest appointment 11 Feb 2026 · latest resignation 30 Nov 2025',
      },
      {
        k: 'Charges',
        v: '4 outstanding, 2 satisfied',
        note: 'Most recent: fixed and floating, registered 08 Jan 2026',
      },
      { k: 'Accounts', v: 'Filed to 31 Dec 2025', note: 'Full accounts · next due 30 Sep 2026' },
    ],
  },
  {
    label: 'Ownership and control',
    rt: '4 registries traversed',
    tree: [
      { depth: 1, code: 'gb', name: 'Northgate Logistics Limited', note: 'Subject' },
      { depth: 2, code: 'nl', name: 'Northgate Logistics B.V.', note: '100%' },
      { depth: 3, code: 'lu', name: 'Verrick Holdings S.à r.l.', note: '100%' },
    ],
    rows: [
      {
        k: 'Beneficial owner',
        v: 'Natural person, 62.4% indirect',
        note: 'Derived across 3 holdings · each step evidenced to its filing',
        tag: { text: 'Traced', type: 'info' },
      },
      {
        k: 'Screening',
        v: 'Every entity and officer in the chain screened',
        note: 'OFAC · EU · UK OFSI · UN consolidated · PEP',
        tag: { text: 'No match', type: 'ok' },
      },
      {
        k: 'Intermediate hit',
        v: 'One director of the Luxembourg holder is a domestic PEP',
        note: 'Office held since 2022 · enhanced due diligence indicated',
        tag: { text: 'PEP', type: 'hit' },
      },
    ],
  },
  {
    label: 'Screening — full chain',
    rt: '6 entities · 14 officers',
    rows: [
      {
        k: 'Lists',
        v: 'OFAC SDN, EU consolidated, UK OFSI, UN consolidated',
        note: 'Re-screened on every list update, not on a schedule',
        tag: { text: 'Current', type: 'ok' },
      },
      {
        k: 'Scope',
        v: 'Every entity and officer in the ownership chain',
        note: 'Screening the subject alone would have missed the hit below',
        tag: { text: 'Full chain', type: 'info' },
      },
      {
        k: 'Aggregated holdings',
        v: 'Combined stakes tested, not only direct ones',
        note: 'A holder under the threshold on its own can cross it in aggregate',
        tag: { text: 'Tested', type: 'info' },
      },
      {
        k: 'Result',
        v: 'One domestic PEP. No sanctions match.',
        note: 'Director of the Luxembourg holder, office held since 2022',
        tag: { text: 'PEP', type: 'warn' },
      },
      {
        k: 'Adjudication',
        v: 'Each hit carries the entry it matched',
        note: 'List, entry reference and the date that entry was published',
      },
    ],
  },
  {
    label: 'Filed accounts',
    rtFlag: 'gb',
    rt: 'Companies House',
    rows: [
      {
        k: 'Latest accounts',
        v: 'Year to 31 Dec 2025',
        note: 'Full accounts, filed 18 Jul 2026 · next due 30 Sep 2026',
        tag: { text: 'Filed', type: 'ok' },
      },
      { k: 'Turnover', v: '£48.2m', note: 'As reported in the filed accounts' },
      {
        k: 'Net assets',
        v: '£6.9m',
        note: 'Down £4.5m from £11.4m at 31 Dec 2024, a 39% fall',
        tag: { text: 'Trend', type: 'warn' },
      },
      {
        k: 'History',
        v: 'Five years of accounts, held comparably',
        note: 'Restated where the entity changed reporting regime',
      },
      {
        k: 'Coverage',
        v: 'Depth follows the filing regime',
        note: 'Small-company and micro-entity filings publish less. Shown as filed, with the gap left visible.',
        tag: { text: 'Note', type: 'info' },
      },
    ],
  },
  {
    label: 'Register monitoring',
    live: true,
    rt: 'Watching since 14 Feb 2026',
    rows: [
      {
        k: '18 Jul 2026',
        v: 'Charge registered — fixed and floating over all assets',
        note: 'Chargee: a lender not previously in the file',
        tag: { text: 'Security', type: 'warn' },
      },
      {
        k: '02 Jun 2026',
        v: 'Director appointed',
        note: 'Also a director of two entities outside the mapped group',
        tag: { text: 'Officer', type: 'info' },
      },
      {
        k: '29 Apr 2026',
        v: 'PSC statement updated — control moved above the Dutch holder',
        note: 'Ownership chain re-traced automatically',
        tag: { text: 'Control', type: 'warn' },
      },
      {
        k: '11 Mar 2026',
        v: 'Registered office changed',
        note: 'Address for service updated in the matter record',
        tag: { text: 'Address', type: 'info' },
      },
      {
        k: '14 Feb 2026',
        v: 'Sister entity entered administration',
        note: 'Northgate Cold Chain Ltd · CRO Ireland',
        tag: { text: 'Insolvency', type: 'hit' },
      },
    ],
  },
];

export const LegalLifeOfMatter = () => {
  const { t } = useInt();
  const steps = getSteps(t);
  const proof = getProof(t);
  const spacerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = spacerRefs.current.indexOf(entry.target as HTMLDivElement);
            if (index !== -1) {
              setActive(index);
            }
          }
        });
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 },
    );

    spacerRefs.current.forEach((el) => el && observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          theme="dark"
          title={t('One entity, followed from intake to closure.')}
          description={t(
            'Six checks, in the order a matter actually needs them. Each one hands its result to the next, and all six run on the same record.',
          )}
        />
      </div>

      <div className={styles.grid}>
        <div className={styles.railCol}>
          <div className={styles.rail}>
            {steps.map((step, i) => (
              <div className={cn(styles.railItem, active === i && styles.railItemOn)} key={step.k}>
                <span className={styles.railK}>{step.k}</span>
                <span className={styles.railT}>{step.t}</span>
                <span className={styles.railD}>{step.d}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.stageCol}>
          <div className={styles.stage}>
            <div className={styles.stageInner}>
              {PANELS.map((panel, i) => (
                <div className={cn(styles.panel, active === i && styles.panelOn)} key={panel.label}>
                  <div className={styles.art}>
                    <div className={styles.artHead}>
                      <span className={styles.artLbl}>
                        {panel.live && <i className={styles.liveDot} />}
                        {panel.label}
                      </span>
                      <span className={styles.artRt}>
                        {panel.rtFlag && <Flag code={panel.rtFlag} className={styles.rtFlag} />}
                        {panel.rt}
                      </span>
                    </div>

                    {panel.tree && (
                      <div className={styles.tree}>
                        {panel.tree.map((node) => (
                          <div className={cn(styles.tnode, styles[`depth${node.depth}`])} key={node.name}>
                            <Flag code={node.code} className={styles.tnodeFlag} />
                            <span className={styles.tnodeName}>{node.name}</span>
                            <span className={styles.tnodeNote}>{node.note}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {panel.rows.map((row) => (
                      <div className={styles.row} key={row.k}>
                        <span className={styles.rowK}>{row.k}</span>
                        <span className={styles.rowV}>
                          {row.v}
                          {row.note && <small>{row.note}</small>}
                        </span>
                        {row.tag && (
                          <span className={cn(styles.tag, styles[`tag${row.tag.type}`])}>{row.tag.text}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.spacers} aria-hidden="true">
            {steps.map((step, i) => (
              <div
                ref={(el) => {
                  spacerRefs.current[i] = el;
                }}
                key={step.k}
              />
            ))}
          </div>
          <div className={styles.spacerTail} aria-hidden="true" />
        </div>
      </div>

      <div className={styles.proof}>
        {proof.map((text, i) => (
          <div className={styles.proofItem} key={text}>
            <span className={styles.proofIdx}>{String(i + 1).padStart(2, '0')}</span>
            <span className={styles.proofRule} />
            <p className={styles.proofT}>{text}</p>
          </div>
        ))}
      </div>
    </section>
  );
};
